import { useId, useState } from "react";

import { useApi } from "../../api/ApiProvider";
import { errorMessage, useConsoleConfig } from "../../api/queries";
import { formatFromFilename } from "../../lib/csvHeader";
import { useUploads } from "../../upload/UploadProvider";
import { LoadingBlock } from "../ui/StateBlock";

interface AddFileProps {
  datasetId: string;
  /** What the upload strip calls this transfer: "Acme SA · churn 2026". */
  datasetLabel: string;
}

/**
 * Add a file to a dataset that already exists.
 *
 * The wizard is not the only place a version is started, and it never was: an
 * upload that was interrupted, or one the console rejected because the bytes
 * never arrived, leaves a dataset whose only recovery used to be inventing a
 * second name for the same client file — which is the duplicate-file problem
 * this product exists to end. `POST /datasets/{id}/versions` already allocates
 * the next number; this is the control that calls it.
 *
 * Args:
 *     datasetId: Which dataset gets the new version.
 *     datasetLabel: What the upload strip shows beside the progress.
 *
 * Returns:
 *     The control.
 */
export function AddFile({ datasetId, datasetLabel }: AddFileProps): JSX.Element {
  const fileId = useId();
  const api = useApi();
  const uploads = useUploads();
  const config = useConsoleConfig();
  const [starting, setStarting] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const take = async (chosen: File): Promise<void> => {
    setStarting(true);
    setFailure(null);
    // A name is a declaration: the console reads the file itself and says so
    // if it disagrees, so an extension it does not recognise is declared as
    // the format this console takes and settled by the bytes.
    const format = formatFromFilename(chosen.name) ?? "csv";
    try {
      const version = await api.createVersion(datasetId, {
        filename: chosen.name,
        format,
        size_bytes: chosen.size,
      });
      if (version.upload) {
        uploads.start({
          datasetId,
          datasetLabel,
          versionNumber: version.number,
          file: chosen,
          format,
          uploadId: version.upload.upload_id,
          parts: version.upload.parts,
        });
      }
    } catch (error) {
      setFailure(error);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="add-file">
      <div
        className="dropzone"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          const dropped = event.dataTransfer.files[0];
          if (dropped) {
            void take(dropped);
          }
        }}
      >
        <label className="dropzone__label" htmlFor={fileId}>
          Drop another file here, or choose one
        </label>
        <input
          id={fileId}
          className="dropzone__input"
          type="file"
          disabled={starting}
          onChange={(event) => {
            const chosen = event.target.files?.[0];
            if (chosen) {
              void take(chosen);
            }
          }}
        />
        <p className="form__note">
          It becomes the next version. Every version before it stays exactly as
          it was, and still answers what a score came from.
          {config.data ? (
            <>
              {" "}
              CSV or Parquet, up to {config.data.max_upload_gb} GB — a bound on
              one file, not on you.
            </>
          ) : null}
        </p>
      </div>

      {starting ? <LoadingBlock label="Opening the next version…" /> : null}

      {failure ? (
        <p className="form__error" role="alert">
          {errorMessage(failure)}
        </p>
      ) : null}
    </div>
  );
}
